// File: frontend/src/context/ThemeContext.tsx
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import {
  applyFanTheme,
  clearFanTheme,
  extractClubColors,
  loadFanThemeCache,
  saveFanThemeCache,
  FanThemeCache,
} from "../utils/fanTheme";
import { getMyProfileDetails } from "../services/profileService";

// Theme types: light, dark, paper, tango, flamingo, comolokko, lokum, mercan, gokyuzu, taraftar
// "taraftar" is dynamic — its palette is derived at runtime from the colours of
// the user's first favourite club (see utils/fanTheme.ts). Every other theme is
// a fixed [data-theme] block in Variables.css.
export type ThemeType = "light" | "dark" | "paper" | "tango" | "flamingo" | "comolokko" | "lokum" | "mercan" | "gokyuzu" | "taraftar";

const VALID_THEMES: ThemeType[] = [
  "light", "dark", "paper", "tango", "flamingo", "comolokko", "lokum", "mercan", "gokyuzu", "taraftar",
];

// Theme used whenever "taraftar" is selected but the user has no favourite club
// with usable colours — the theme must never render as a half-painted page.
const FAN_FALLBACK_THEME: ThemeType = "light";

interface ThemeContextType {
  theme: ThemeType;
  setTheme: (theme: ThemeType) => void;
  // Colours currently driving the taraftar theme; empty when unavailable.
  fanColors: string[];
  // True when the user has at least one favourite club with stored colours.
  // The theme picker hides the taraftar option entirely when this is false.
  fanThemeAvailable: boolean;
  // Called after the user edits their favourite clubs so the live page repaints
  // without a reload. Passing null clears the fan colours (no clubs left).
  setFanThemeSource: (cache: FanThemeCache | null) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = "app-theme";

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setThemeState] = useState<ThemeType>(() => {
    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
    if (savedTheme && VALID_THEMES.includes(savedTheme as ThemeType)) {
      return savedTheme as ThemeType;
    }
    return "light"; // Default theme
  });

  // Seeded from localStorage so a reload repaints on the first frame instead of
  // flashing the fallback palette while the profile request is in flight.
  const [fanCache, setFanCache] = useState<FanThemeCache | null>(() => loadFanThemeCache());

  const setTheme = useCallback((newTheme: ThemeType) => {
    setThemeState(newTheme);
    localStorage.setItem(THEME_STORAGE_KEY, newTheme);
  }, []);

  const setFanThemeSource = useCallback((cache: FanThemeCache | null) => {
    setFanCache(cache);
    saveFanThemeCache(cache);
  }, []);

  // Apply theme to document
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  // Paint (or remove) the dynamic fan palette whenever the theme or the club
  // colours change. Runs after the data-theme attribute above, so the inline
  // properties always sit on top of the matching CSS block.
  useEffect(() => {
    if (theme !== "taraftar") {
      clearFanTheme();
      return;
    }
    const colors = fanCache?.colors ?? [];
    if (colors.length === 0 || !applyFanTheme(colors)) {
      // Nothing to paint with — the [data-theme="taraftar"] fallback block keeps
      // the page readable until the refresh below decides what to do.
      clearFanTheme();
    }
  }, [theme, fanCache]);

  // Read the user's first favourite club once per app load, and again whenever
  // the theme changes. Two reasons it is not limited to the taraftar theme:
  // the header's theme cycle has to know whether the option exists at all, and
  // a user who changed clubs on another device must not stay on stale colours.
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      // Guests cannot have favourite clubs — the theme is meaningless for them.
      setFanThemeSource(null);
      if (theme === "taraftar") setTheme(FAN_FALLBACK_THEME);
      return;
    }

    let cancelled = false;
    getMyProfileDetails()
      .then((details) => {
        if (cancelled) return;
        const primaryClub = details.favoriteClubs?.[0] ?? null;
        const colors = extractClubColors(primaryClub);
        if (colors.length === 0) {
          // No favourite club, or the club has no verified colours stored.
          setFanThemeSource(null);
          if (theme === "taraftar") setTheme(FAN_FALLBACK_THEME);
          return;
        }
        setFanThemeSource({
          clubId: primaryClub?.id ?? null,
          clubName: primaryClub?.shortName ?? null,
          colors,
        });
      })
      .catch(() => {
        // Network or auth failure: keep whatever is cached rather than throwing
        // the user onto a different theme because one request failed.
      });

    return () => { cancelled = true; };
  // setTheme / setFanThemeSource are stable useCallbacks; re-running on theme only
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme]);

  const fanColors = fanCache?.colors ?? [];

  return (
    <ThemeContext.Provider
      value={{
        theme,
        setTheme,
        fanColors,
        fanThemeAvailable: fanColors.length > 0,
        setFanThemeSource,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};